// src/context/CartContext.jsx - Enhanced with discount system and user authentication
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { configApi, discountApi, cartApi } from '../services/api';
import { useDiscounts } from './DiscountContext';
import { useAuth } from '../hooks/useAuth';
import { trackAddToCart, trackRemoveFromCart } from '../utils/ga4Events';
import CartAddedDrawer from '../components/cart/CartAddedDrawer';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { getCartDiscounts } = useDiscounts();
  const { user, isAuthenticated } = useAuth();
  const [isCartOperationPending, setIsCartOperationPending] = useState(false);
  const [cartFeedback, setCartFeedback] = useState({
    isOpen: false,
    item: null,
    message: null,
    itemCount: null,
  });

  // Helper function to normalize cart items - defined before useState to use in initializer
  const normalizeCartItems = useCallback((items) => {
    if (!Array.isArray(items)) return [];

    return items.map(item => {
      const { discountedPrice, hasAppliedDiscount, effectivePrice, ...cleanItem } = item;

      // If localStorage has nested product structure, normalize it
      if (cleanItem.product && cleanItem.product.id) {
        return {
          ...cleanItem.product,  // Use product data as base
          id: cleanItem.product.id,  // Use product ID as main ID
          quantity: cleanItem.quantity,
          unitPrice: cleanItem.unitPrice || cleanItem.priceAtTime || cleanItem.product.price,
          // Keep server cart item ID for reference if needed
          cartItemId: cleanItem.id,
          // Preserve any cart-specific data
          addedAt: cleanItem.addedAt,
          lineTotal: cleanItem.lineTotal
        };
      }

      return cleanItem; // Already normalized
    });
  }, []);

  // Helper function to create a hash of cart items for change detection
  const hashCart = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return 'empty';

    // Create a deterministic hash based on product IDs and quantities
    const cartSignature = items
      .map(item => `${item.id || item.product?.id}:${item.quantity}`)
      .sort()
      .join('|');

    return cartSignature;
  }, []);

  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      console.log('🔍 [INIT] Reading cart from localStorage on component mount...');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        console.log('✅ [INIT] Found cart in localStorage:', {
          itemCount: parsedCart.length,
          itemIds: parsedCart.map(i => i.id),
          items: parsedCart.map(i => ({ id: i.id, name: i.name, quantity: i.quantity }))
        });
        // Clean any stale discount data and normalize structure from localStorage
        const normalized = parsedCart.map(item => {
          const { discountedPrice, hasAppliedDiscount, effectivePrice, ...cleanItem } = item;

          // If localStorage has nested product structure, normalize it
          if (cleanItem.product && cleanItem.product.id) {
            return {
              ...cleanItem.product,  // Use product data as base
              id: cleanItem.product.id,  // Use product ID as main ID
              quantity: cleanItem.quantity,
              unitPrice: cleanItem.unitPrice || cleanItem.priceAtTime || cleanItem.product.price,
              // Keep server cart item ID for reference if needed
              cartItemId: cleanItem.id,
              // Preserve any cart-specific data
              addedAt: cleanItem.addedAt,
              lineTotal: cleanItem.lineTotal
            };
          }

          return cleanItem; // Already normalized
        });
        console.log('✅ [INIT] Normalized cart items:', normalized.length);
        return normalized;
      }
      console.log('ℹ️ [INIT] No cart found in localStorage - starting with empty cart');
      return [];
    } catch (error) {
      console.error('❌ [INIT] Error reading cart from localStorage:', error);
      return [];
    }
  });

  const [taxAndShippingRates, setTaxAndShippingRates] = useState({
    taxRate: 0.18, // Default 18% VAT
    shippingCost: 80.00, // Default shipping cost
    freeShippingThreshold: 500.00 // Default free shipping threshold
  });

  const [ratesLoading, setRatesLoading] = useState(true);
  
  // Discount system states
  const [discountCalculation, setDiscountCalculation] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  /** Checkout coupon (sent to /cart/calculate & checkout); normalized on server */
  const [checkoutDiscountCode, setCheckoutDiscountCode] = useState('');
  const [isLoadingUserCart, setIsLoadingUserCart] = useState(false);
  const [hasLoadedUserCart, setHasLoadedUserCart] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isClearingCart, setIsClearingCart] = useState(false);
  
  // Debounce timer for discount calculations
  const discountCalculationTimer = useRef(null);
  const cartMergeTimer = useRef(null);
  const lastSyncedCart = useRef(null);
  const lastSyncTime = useRef(0);
  const MIN_SYNC_INTERVAL = 2000; // Minimum 2 seconds between syncs

  // Cross-tab synchronization
  const backendPollInterval = useRef(null);
  const lastBackendPollTime = useRef(0);
  const MIN_POLL_INTERVAL = 60000; // Poll backend every 60 seconds
  const lastCartHash = useRef('empty'); // Initialize with 'empty', will be set properly in effect

  // Session-based merge timeout (30 minutes)
  // After this time, we'll merge with backend again to sync cross-device changes
  const SESSION_MERGE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

  // Fetch tax and shipping rates from API
  useEffect(() => {
    const fetchRates = async () => {
      try {
        setRatesLoading(true);
        const rates = await configApi.getRates();

        // Ensure we have valid numeric values
        const validatedRates = {
          taxRate: typeof rates.taxRate === 'number' ? rates.taxRate : 0.18,
          shippingCost: typeof rates.shippingCost === 'number' ? rates.shippingCost : 80.00,
          freeShippingThreshold: typeof rates.freeShippingThreshold === 'number' ? rates.freeShippingThreshold : 500.00
        };


        setTaxAndShippingRates(validatedRates);
      } catch (error) {

        // Keep default rates if API fails
      } finally {
        setRatesLoading(false);
      }
    };

    fetchRates();
  }, []);

  useEffect(() => {
    // Note: localStorage is now saved synchronously in add/remove/update functions
    // This useEffect only serves as a backup safety net for any edge cases
    const itemsToSave = cartItems.map(item => {
      const { discountedPrice, hasAppliedDiscount, effectivePrice, ...baseItem } = item;
      return baseItem;
    });
    const cartItemsStr = JSON.stringify(itemsToSave);

    // Only save if it's actually different from what's in localStorage
    // This prevents duplicate storage events
    const currentStorage = localStorage.getItem('cart');
    if (currentStorage !== cartItemsStr) {
      localStorage.setItem('cart', cartItemsStr);
      console.log('💾 📋 useEffect backup save to localStorage', {
        itemCount: itemsToSave.length
      });
    }

    // Debounced discount recalculation when cart changes
    if (cartItems.length > 0) {
      // Clear existing timer
      if (discountCalculationTimer.current) {
        clearTimeout(discountCalculationTimer.current);
      }

      // Set new timer (500ms debounce) - inline to avoid dependency issue
      discountCalculationTimer.current = setTimeout(() => {
        calculateCartDiscounts();
      }, 500);
    } else {
      // Clear debounce timer and immediately clear discounts for empty cart
      if (discountCalculationTimer.current) {
        clearTimeout(discountCalculationTimer.current);
        discountCalculationTimer.current = null;
      }
      setDiscountCalculation(null);
    }
  }, [cartItems]); // Remove debouncedCalculateCartDiscounts from deps

  const runCartDiscountRequest = useCallback(async (couponCodeForRequest) => {
    if (cartItems.length === 0) {
      setDiscountCalculation(null);
      return null;
    }

    setDiscountLoading(true);
    try {
      const cartItemsForApi = cartItems.map(item => ({
        productId: item.id,
        quantity: item.quantity,
        price: item.unitPrice || item.priceAtTime || item.price || 0
      }));

      const trimmed =
        couponCodeForRequest != null && String(couponCodeForRequest).trim()
          ? String(couponCodeForRequest).trim()
          : undefined;

      const calculation = await discountApi.calculateCartDiscounts(
        cartItemsForApi,
        customerEmail,
        trimmed
      );
      setDiscountCalculation(calculation);
      return calculation;
    } catch (error) {
      setDiscountCalculation(null);
      return null;
    } finally {
      setDiscountLoading(false);
    }
  }, [cartItems, customerEmail]);

  const calculateCartDiscounts = useCallback(() => {
    return runCartDiscountRequest(checkoutDiscountCode);
  }, [runCartDiscountRequest, checkoutDiscountCode]);

  const applyCheckoutDiscountCode = useCallback(
    async (raw) => {
      const t = (raw || '').trim();
      setCheckoutDiscountCode(t);
      const calc = await runCartDiscountRequest(t);
      if (!t) {
        return { ok: true, error: null };
      }
      if (!calc) {
        return { ok: false, error: 'NETWORK' };
      }
      if (calc.discountCodeError) {
        return { ok: false, error: calc.discountCodeError };
      }
      return { ok: true, error: null };
    },
    [runCartDiscountRequest]
  );

  const clearCheckoutDiscountCode = useCallback(async () => {
    setCheckoutDiscountCode('');
    await runCartDiscountRequest('');
  }, [runCartDiscountRequest]);

  // Debounced version of calculateCartDiscounts to prevent excessive API calls
  // Using ref pattern to avoid circular dependencies
  const calculateCartDiscountsRef = useRef(calculateCartDiscounts);
  calculateCartDiscountsRef.current = calculateCartDiscounts;

  const debouncedCalculateCartDiscounts = useCallback(() => {
    // Clear existing timer
    if (discountCalculationTimer.current) {
      clearTimeout(discountCalculationTimer.current);
    }

    // Set new timer (500ms debounce)
    discountCalculationTimer.current = setTimeout(() => {
      calculateCartDiscountsRef.current();
    }, 500);
  }, []); // No dependencies - uses ref

  // Sync cart with server when user is authenticated - DEFINED FIRST to avoid circular dependency
  const syncCartWithServer = useCallback(async () => {
    if (!isAuthenticated || !user || isLoadingUserCart || isSyncing) {
      console.log('🚫 Sync blocked:', { isAuthenticated, user: !!user, isLoadingUserCart, isSyncing });
      return;
    }

    // Rate limiting - prevent too frequent syncs
    const now = Date.now();
    if (now - lastSyncTime.current < MIN_SYNC_INTERVAL) {
      console.log('⏱️ Sync rate limited - too soon since last sync');
      return;
    }

    // Check if cart has actually changed since last sync
    const cartItemsStr = JSON.stringify(cartItems);
    if (lastSyncedCart.current === cartItemsStr) {
      console.log('⏭️ Sync skipped - cart unchanged');
      return; // Skip sync if cart hasn't changed
    }

    console.log('🔄 Starting cart sync...', { cartItemsLength: cartItems.length });
    setIsSyncing(true);
    lastSyncTime.current = now;
    try {
      await cartApi.syncCart(cartItems);
      lastSyncedCart.current = cartItemsStr; // Remember what we synced
      console.log('✅ Cart sync completed successfully');
    } catch (error) {
      console.warn('❌ Failed to sync cart with server:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isAuthenticated, user, cartItems, isLoadingUserCart, isSyncing]);

  // Load user's cart from backend - ONLY on fresh login, then merge with local
  const loadUserCartFromBackend = useCallback(async () => {
    if (!isAuthenticated || !user || isLoadingUserCart || hasLoadedUserCart) {
      console.log('⏭️ [LOAD] Skipping backend cart load:', {
        isAuthenticated,
        hasUser: !!user,
        isLoadingUserCart,
        hasLoadedUserCart
      });
      return;
    }

    setIsLoadingUserCart(true);
    try {
      console.log('🔄 [LOAD] Loading user cart from backend (one-time on login)...');

      // Get localStorage cart (what user added while not logged in)
      const localCart = localStorage.getItem('cart');
      const localItems = localCart ? JSON.parse(localCart) : [];
      console.log('📋 [LOAD] Local cart items from localStorage:', {
        itemCount: localItems.length,
        itemIds: localItems.map(i => i.id),
        items: localItems.map(i => ({ id: i.id, name: i.name, quantity: i.quantity }))
      });

      // Get user's saved cart from server
      const userCart = await cartApi.getCart().catch(() => ({ items: [] }));
      const serverItems = userCart.items || [];
      console.log('🌐 [LOAD] Server cart items from backend:', {
        itemCount: serverItems.length,
        itemIds: serverItems.map(i => i.product?.id || i.id),
        items: serverItems.map(i => ({
          id: i.product?.id || i.id,
          name: i.product?.name || i.name,
          quantity: i.quantity
        }))
      });

      // Normalize server items
      const normalizedServerItems = serverItems.map(item => {
        if (item.product && item.product.id) {
          return {
            ...item.product,
            id: item.product.id,
            quantity: item.quantity,
            unitPrice: item.unitPrice || item.priceAtTime || item.product.price,
            cartItemId: item.id,
            addedAt: item.addedAt,
            lineTotal: item.lineTotal
          };
        }
        return item;
      });

      // MERGE strategy: Combine local + server carts (prefer local quantities if conflict)
      const mergedCart = [...localItems];
      console.log('🔀 [LOAD] Starting merge - initial local cart has', mergedCart.length, 'items');

      normalizedServerItems.forEach(serverItem => {
        const existingIndex = mergedCart.findIndex(local => local.id === serverItem.id);
        if (existingIndex === -1) {
          // Item only in server cart - add it
          console.log('➕ [LOAD] Adding item from server (not in local):', {
            id: serverItem.id,
            name: serverItem.name
          });
          mergedCart.push(serverItem);
        } else {
          console.log('⏭️ [LOAD] Skipping server item (already in local):', {
            id: serverItem.id,
            name: serverItem.name,
            localQty: mergedCart[existingIndex].quantity,
            serverQty: serverItem.quantity
          });
        }
        // If item exists in both, keep local version (user just added it)
      });

      console.log('✅ [LOAD] Merge completed:', {
        localItems: localItems.length,
        serverItems: normalizedServerItems.length,
        mergedItems: mergedCart.length,
        finalItemIds: mergedCart.map(i => i.id),
        finalItems: mergedCart.map(i => ({ id: i.id, name: i.name, quantity: i.quantity }))
      });

      // Set merged cart and save to localStorage
      setCartItems(mergedCart);
      localStorage.setItem('cart', JSON.stringify(mergedCart));
      lastSyncedCart.current = JSON.stringify(mergedCart);
      console.log('💾 [LOAD] Saved merged cart to localStorage and state');

      // Sync merged cart back to backend (use setTimeout to avoid circular call)
      if (mergedCart.length > 0) {
        console.log('🔄 [LOAD] Scheduling backend sync of merged cart...');
        setTimeout(() => {
          syncCartWithServer().catch(err => {
            console.warn('Failed to sync merged cart:', err);
          });
        }, 100);
      }

      setHasLoadedUserCart(true);

      // Update the merge timestamp in sessionStorage
      const mergeTimeKey = `cart_last_merge_time_${user.id || user.email}`;
      sessionStorage.setItem(mergeTimeKey, Date.now().toString());
      console.log('⏱️ [LOAD] Updated merge timestamp');
    } catch (error) {
      console.error('❌ [LOAD] Error loading user cart:', error);
      setHasLoadedUserCart(true); // Set to true to prevent retry loops
    } finally {
      setIsLoadingUserCart(false);
    }
  }, [isAuthenticated, user, isLoadingUserCart, hasLoadedUserCart, syncCartWithServer]);

  // Handle user authentication changes - load backend cart on login
  useEffect(() => {
    if (isAuthenticated && user && !hasLoadedUserCart) {
      // Set customer email for discount calculations
      setCustomerEmail(user.email || '');

      // Check if we've EVER loaded backend cart for this user (stored in sessionStorage)
      // sessionStorage persists across page refreshes but NOT across browser close
      const sessionKey = `cart_loaded_${user.id || user.email}`;
      const mergeTimeKey = `cart_last_merge_time_${user.id || user.email}`;

      const hasLoadedInSession = sessionStorage.getItem(sessionKey);
      const lastMergeTime = sessionStorage.getItem(mergeTimeKey);
      const now = Date.now();

      // Check if we need to merge:
      // 1. Never loaded in this session (new browser/tab)
      // 2. OR it's been longer than SESSION_MERGE_TIMEOUT since last merge
      const shouldMerge = !hasLoadedInSession ||
                         !lastMergeTime ||
                         (now - parseInt(lastMergeTime, 10)) > SESSION_MERGE_TIMEOUT;

      if (shouldMerge) {
        const reason = !hasLoadedInSession
          ? 'first load in this session'
          : 'session timeout - syncing cross-device changes';

        console.log(`🔑 Merging with backend (${reason})`);
        console.log(`⏱️ Time since last merge: ${lastMergeTime ? Math.round((now - parseInt(lastMergeTime, 10)) / 1000 / 60) : 'N/A'} minutes`);

        // Merge with backend (for cross-device sync)
        loadUserCartFromBackend();

        // Update session storage
        sessionStorage.setItem(sessionKey, 'true');
        sessionStorage.setItem(mergeTimeKey, now.toString());
      } else {
        const timeSinceLastMerge = Math.round((now - parseInt(lastMergeTime, 10)) / 1000 / 60);
        console.log(`🔄 Already loaded backend cart in this session - trusting localStorage`);
        console.log(`⏱️ Time since last merge: ${timeSinceLastMerge} minutes (will merge again in ${Math.round(SESSION_MERGE_TIMEOUT / 1000 / 60) - timeSinceLastMerge} minutes)`);

        // Already loaded backend cart recently - trust localStorage completely
        // Just mark as loaded and sync current local cart to backend
        setHasLoadedUserCart(true);

        // Sync current localStorage cart to backend (one-way: local → backend)
        setTimeout(() => {
          syncCartWithServer().catch(err => {
            console.warn('Failed to sync local cart to backend:', err);
          });
        }, 500);
      }
    } else if (!isAuthenticated && hasLoadedUserCart) {
      // User logged out (not just page refresh), reset state and clear customer email
      setHasLoadedUserCart(false);
      setCustomerEmail('');
      // Clear cart on logout to start fresh for anonymous user
      setCartItems([]);
      setDiscountCalculation(null);
      setCheckoutDiscountCode('');
      lastSyncedCart.current = null;

      // Clear session storage for all users
      const sessionKeys = Object.keys(sessionStorage).filter(key =>
        key.startsWith('cart_loaded_') || key.startsWith('cart_last_merge_time_')
      );
      sessionKeys.forEach(key => sessionStorage.removeItem(key));
    }
  }, [isAuthenticated, user, hasLoadedUserCart, syncCartWithServer, loadUserCartFromBackend]);

  // Debounced cart sync to avoid excessive API calls
  // Using useRef to avoid recreating function and causing infinite loops
  const syncCartWithServerRef = useRef(syncCartWithServer);
  syncCartWithServerRef.current = syncCartWithServer;
  
  const debouncedSyncCart = useCallback(() => {
    if (cartMergeTimer.current) {
      clearTimeout(cartMergeTimer.current);
    }
    
    cartMergeTimer.current = setTimeout(() => {
      syncCartWithServerRef.current();
    }, 1000); // 1 second debounce for cart sync
  }, []); // No dependencies to prevent recreation

  // Sync cart with server when cart changes (for authenticated users)
  useEffect(() => {
    console.log('🔍 Sync useEffect triggered:', { 
      isAuthenticated, 
      user: !!user, 
      hasLoadedUserCart, 
      cartItemsLength: cartItems.length,
      isClearingCart
    });
    
    // Don't sync if we're currently clearing the cart
    if (isClearingCart) {
      console.log('🚫 Sync blocked - cart being cleared');
      return;
    }
    
    // Only sync after user cart has been loaded from backend
    if (isAuthenticated && user && hasLoadedUserCart) {
      console.log('⚡ Triggering debounced sync');
      debouncedSyncCart();
    }
    // Note: debouncedSyncCart is NOT in dependencies to prevent recreation loops
  }, [isAuthenticated, user, hasLoadedUserCart, cartItems, isClearingCart]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (discountCalculationTimer.current) {
        clearTimeout(discountCalculationTimer.current);
      }
      if (cartMergeTimer.current) {
        clearTimeout(cartMergeTimer.current);
      }
      if (backendPollInterval.current) {
        clearInterval(backendPollInterval.current);
      }
    };
  }, []);

  // ========================================
  // CROSS-TAB SYNCHRONIZATION SYSTEM (localStorage-First)
  // ========================================
  // Strategy: All cart operations save to localStorage FIRST (synchronous)
  // Storage events automatically propagate to other tabs (built-in browser feature)
  // Backend sync happens asynchronously in the background
  // This is simpler and more reliable than BroadcastChannel

  // 0. BeforeUnload Handler - Force sync when tab is closing
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      console.log('⚠️ Tab closing - forcing immediate cart sync');

      // If user is authenticated and cart has unsaved changes
      if (isAuthenticated && user && cartItems.length >= 0) {
        const cartItemsStr = JSON.stringify(cartItems);

        // Check if there are unsaved changes
        if (lastSyncedCart.current !== cartItemsStr) {
          console.log('💾 Unsaved cart changes detected, forcing sync...');

          // Use navigator.sendBeacon for reliable background sync
          // This works even after tab is closed
          const syncData = JSON.stringify({
            cartItems: cartItems,
            timestamp: Date.now()
          });

          // Try sendBeacon first (most reliable for page unload)
          const beaconUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api'}/user/cart/sync`;
          const token = localStorage.getItem('token');

          if (navigator.sendBeacon && token) {
            const blob = new Blob([syncData], { type: 'application/json' });
            const formData = new FormData();
            formData.append('data', blob);

            try {
              // SendBeacon is fire-and-forget, no await needed
              const sent = navigator.sendBeacon(beaconUrl, blob);
              console.log(sent ? '✅ Beacon sent successfully' : '⚠️ Beacon failed');
            } catch (error) {
              console.error('❌ Beacon error:', error);
            }
          }

          // Also try synchronous fetch as fallback
          try {
            // Use fetch with keepalive flag for page unload
            fetch(beaconUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: syncData,
              keepalive: true // Critical: keeps request alive after page unload
            }).catch(() => {
              // Ignore errors - best effort
            });
          } catch (error) {
            // Ignore - we tried our best
          }

          // Mark as synced to prevent duplicate attempts
          lastSyncedCart.current = cartItemsStr;
        }
      }

      // Don't show confirmation dialog
      // delete event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    console.log('✅ BeforeUnload sync handler registered');

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      console.log('🔒 BeforeUnload sync handler removed');
    };
  }, [isAuthenticated, user, cartItems]);

  // 1. Storage Event Listener - PRIMARY cross-tab sync mechanism
  // This is THE MAIN WAY tabs communicate - simpler and more reliable than BroadcastChannel
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Only respond to cart key changes
      if (event.key !== 'cart') return;

      // Ignore if no new value (cart was cleared)
      if (!event.newValue) {
        console.log('💾 Cart cleared in another tab');
        setCartItems([]);
        setDiscountCalculation(null);
        setCheckoutDiscountCode('');
        return;
      }

      console.log('💾 📥 Cart updated in another tab - syncing...');

      try {
        const updatedCart = JSON.parse(event.newValue);
        const normalizedItems = normalizeCartItems(updatedCart);

        console.log('💾 ✅ Received cart from another tab:', {
          itemCount: normalizedItems.length,
          items: normalizedItems.map(i => `${i.name} (qty: ${i.quantity})`)
        });

        // Update state with the new cart
        setCartItems(normalizedItems);
        lastCartHash.current = hashCart(normalizedItems);

        // Recalculate discounts for new cart
        if (normalizedItems.length > 0) {
          debouncedCalculateCartDiscounts();
        } else {
          setDiscountCalculation(null);
        }

        console.log('💾 ✅ Cart synced successfully from another tab!');
      } catch (error) {
        console.error('❌ Error syncing cart from storage event:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    console.log('✅ Storage event listener registered (cross-tab sync active)');

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [normalizeCartItems, hashCart, debouncedCalculateCartDiscounts]);

  // REMOVED: Visibility API and Backend Polling
  // These were OVERWRITING localStorage with stale backend data!
  //
  // Industry-standard approach:
  // - localStorage is THE ONLY source of truth for the UI
  // - Backend is just for persistence (one-way: local → backend)
  // - Backend NEVER overwrites local cart except on fresh login

  // ========================================
  // END CROSS-TAB SYNCHRONIZATION SYSTEM
  // ========================================
  // Note: We removed BroadcastChannel - localStorage + storage events are simpler and more reliable

  // Calculate effective price for bulk discounts (still needed for product detail page)
  const calculateEffectivePrice = useCallback((product, quantity) => {
    if (!product || typeof product.price !== 'number') {
      return product?.price || 0;
    }

    const discounts = product.bulkDiscounts;
    if (!discounts || !Array.isArray(discounts) || discounts.length === 0) {
      return product.price;
    }

    const applicableDiscount = [...discounts]
      .filter(discount => discount && discount.isActive !== false && typeof discount.minQuantity === 'number' && discount.minQuantity <= quantity)
      .sort((a, b) => b.minQuantity - a.minQuantity)[0];

    if (!applicableDiscount) {
      return product.price;
    }

    let discountedPrice = product.price;
    if (applicableDiscount.discountType === 'PERCENTAGE' && typeof applicableDiscount.discountPercentage === 'number') {
      discountedPrice = product.price * (1 - (applicableDiscount.discountPercentage / 100));
    } else if (applicableDiscount.discountType === 'FIXED_AMOUNT' && typeof applicableDiscount.discountAmount === 'number') {
      discountedPrice = product.price - applicableDiscount.discountAmount;
    }

    return parseFloat((discountedPrice > 0 ? discountedPrice : 0).toFixed(2));
  }, []);

  const closeCartAddedFeedback = useCallback(() => {
    setCartFeedback((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const showCartAddedFeedback = useCallback((payload = {}) => {
    setCartFeedback({
      isOpen: true,
      item: payload.item || null,
      message: payload.message || null,
      itemCount: payload.itemCount || null,
    });
  }, []);

  const addToCart = useCallback(async (productData, options = {}) => {
    const { showFeedback = true } = options;

    if (!productData || !productData.id || typeof productData.price !== 'number') {
      console.error('addToCart: Invalid product data received', productData);
      return;
    }

    const productId = productData.product?.id || productData.id;
    const quantityToAdd = productData.quantity || 1;

    // Track GA4 add_to_cart event
    try {
      trackAddToCart({
        id: productId,
        name: productData.name || productData.title,
        price: productData.price,
        quantity: quantityToAdd,
        category: productData.category,
        brand: productData.brand,
        variant: productData.variant
      }, productData.price * quantityToAdd, {
        email: user?.email || null,
        externalId: user?.id || user?.email || null,
      });

    } catch (error) {
      console.error('Error tracking add_to_cart event:', error);
    }

    // Update local state AND localStorage synchronously in the same operation
    let updatedCartItems;
    setCartItems(currentItems => {
      const existingItemIndex = currentItems.findIndex(item => item.id === productId);

      if (existingItemIndex > -1) {
        const updatedItems = [...currentItems];
        const existingItem = updatedItems[existingItemIndex];
        const newQuantity = existingItem.quantity + quantityToAdd;

        // Clear discount-specific fields to prevent stale data
        const { discountedPrice, hasAppliedDiscount, effectivePrice, ...baseItem } = existingItem;

        updatedItems[existingItemIndex] = {
          ...baseItem,
          quantity: newQuantity,
          unitPrice: baseItem.unitPrice || productData.price
        };
        updatedCartItems = updatedItems;
        return updatedItems;
      } else {
        // Clean product data - remove any discount fields and normalize structure
        const { discountedPrice, hasAppliedDiscount, effectivePrice, ...cleanProductData } = productData;

        // If productData has nested product structure, flatten it
        const normalizedProductData = productData.product ? {
          ...productData.product,
          id: productData.product.id,  // Use product ID as main ID
          quantity: quantityToAdd,
          unitPrice: productData.unitPrice || productData.price || productData.product.price
        } : {
          ...cleanProductData,
          quantity: quantityToAdd,
          unitPrice: productData.price
        };

        updatedCartItems = [...currentItems, normalizedProductData];
        return updatedCartItems;
      }
    });

    // CRITICAL: Save to localStorage SYNCHRONOUSLY right here
    // Cannot wait for useEffect - tab might close/refresh before it runs!
    if (updatedCartItems) {
      const itemsToSave = updatedCartItems.map(item => {
        const { discountedPrice, hasAppliedDiscount, effectivePrice, ...baseItem } = item;
        return baseItem;
      });
      localStorage.setItem('cart', JSON.stringify(itemsToSave));
      console.log('💾 ⚡ IMMEDIATELY saved to localStorage in addToCart', {
        itemCount: itemsToSave.length,
        timestamp: Date.now()
      });

      // ALSO sync to backend IMMEDIATELY for authenticated users
      // Cannot wait for debounced sync - backend must have latest data
      if (isAuthenticated && user) {
        syncCartWithServer().catch(err => {
          console.warn('⚠️ Immediate backend sync failed (will retry):', err);
        });
      }
    }

    if (showFeedback) {
      showCartAddedFeedback({
        item: {
          id: productId,
          name: productData.name || productData.title,
          image: productData.image || productData.images?.[0],
          price: productData.price ?? productData.unitPrice,
          quantity: quantityToAdd,
        },
      });
    }
  }, [isAuthenticated, user, syncCartWithServer, showCartAddedFeedback]);

  const removeFromCart = useCallback(async (productId) => {
    if (isCartOperationPending) {
      console.log('⏳ Cart operation already in progress, skipping...');
      return;
    }

    setIsCartOperationPending(true);
    console.log('🗑️ removeFromCart called with productId:', productId);
    console.log('🛒 Current cart items BEFORE removal:', cartItems.map(item => ({ id: item.id, cartItemId: item.cartItemId, name: item.name })));

    // Clear discount calculation immediately to prevent showing stale discounts
    setDiscountCalculation(null);

    // Find the item to get its cart item ID for server removal
    const itemToRemove = cartItems.find(item => item.id === productId);
    console.log('🎯 Found item to remove:', itemToRemove);

    if (!itemToRemove) {
      console.error('❌ Item not found in cart for removal:', productId);
      setIsCartOperationPending(false);
      return;
    }

    // Track GA4 remove_from_cart event
    try {
      trackRemoveFromCart({
        id: productId,
        name: itemToRemove.name || itemToRemove.title,
        price: itemToRemove.unitPrice || itemToRemove.price,
        quantity: itemToRemove.quantity || 1,
        category: itemToRemove.category,
        brand: itemToRemove.brand,
        variant: itemToRemove.variant
      }, (itemToRemove.unitPrice || itemToRemove.price) * (itemToRemove.quantity || 1));
    } catch (error) {
      console.error('Error tracking remove_from_cart event:', error);
    }

    // Update local state immediately for better UX
    const updatedItems = cartItems.filter(item => item.id !== productId);
    setCartItems(updatedItems);
    console.log('✅ Updated local cart items AFTER removal:', updatedItems.length, 'items remaining');
    console.log('📋 Items in updated cart:', updatedItems.map(item => ({ id: item.id, name: item.name })));

    // CRITICAL: Save to localStorage SYNCHRONOUSLY right here
    const itemsToSave = updatedItems.map(item => {
      const { discountedPrice, hasAppliedDiscount, effectivePrice, ...baseItem } = item;
      return baseItem;
    });
    localStorage.setItem('cart', JSON.stringify(itemsToSave));
    console.log('💾 ⚡ IMMEDIATELY saved to localStorage in removeFromCart', {
      itemCount: itemsToSave.length,
      itemIds: itemsToSave.map(i => i.id),
      timestamp: Date.now()
    });

    // Verify localStorage was saved correctly
    const verifyStorage = localStorage.getItem('cart');
    const parsedVerify = JSON.parse(verifyStorage);
    console.log('✅ VERIFIED localStorage contents after save:', {
      itemCount: parsedVerify.length,
      itemIds: parsedVerify.map(i => i.id)
    });

    // ALSO sync to backend IMMEDIATELY for authenticated users
    if (isAuthenticated && user) {
      console.log('🔄 Triggering immediate backend sync after removal...');
      syncCartWithServer().catch(err => {
        console.warn('⚠️ Immediate backend sync failed (will retry):', err);
      });
    } else {
      console.log('⏭️ Skipping backend sync - user not authenticated');
    }

    // Immediately trigger discount recalculation after removing item
    if (discountCalculationTimer.current) {
      clearTimeout(discountCalculationTimer.current);
    }
    // Use a shorter timeout for removals to update UI faster
    discountCalculationTimer.current = setTimeout(() => {
      calculateCartDiscounts();
    }, 100);

    setIsCartOperationPending(false);
  }, [cartItems, calculateCartDiscounts, isCartOperationPending, isAuthenticated, user, syncCartWithServer]);

  const updateQuantity = useCallback(async (productId, newQuantity) => {
    if (newQuantity < 1) return;

    // Update local state immediately for better UX
    let updatedCartItems;
    setCartItems(currentItems => {
      updatedCartItems = currentItems.map(item => {
        if (item.id === productId) {
          // Clear discount-specific fields to prevent stale data
          const { discountedPrice, hasAppliedDiscount, effectivePrice, ...baseItem } = item;
          return {
            ...baseItem,
            quantity: newQuantity
          };
        }
        return item;
      });
      return updatedCartItems;
    });

    // CRITICAL: Save to localStorage SYNCHRONOUSLY right here
    if (updatedCartItems) {
      const itemsToSave = updatedCartItems.map(item => {
        const { discountedPrice, hasAppliedDiscount, effectivePrice, ...baseItem } = item;
        return baseItem;
      });
      localStorage.setItem('cart', JSON.stringify(itemsToSave));
      console.log('💾 ⚡ IMMEDIATELY saved to localStorage in updateQuantity', {
        itemCount: itemsToSave.length
      });

      // ALSO sync to backend IMMEDIATELY for authenticated users
      if (isAuthenticated && user) {
        syncCartWithServer().catch(err => {
          console.warn('⚠️ Immediate backend sync failed (will retry):', err);
        });
      }
    }
  }, [cartItems, isAuthenticated, user, syncCartWithServer]);

  // Calculate subtotal (before tax and shipping) - Enhanced with discount system
  const getCartSubtotal = useCallback(() => {
    // Prefer server-calculated subtotal to avoid double counting discounts on UI.
    if (discountCalculation && typeof discountCalculation.finalSubtotal === 'number') {
      return discountCalculation.finalSubtotal;
    }

    // Calculate original subtotal directly
    const originalSubtotal = cartItems.reduce((total, item) => {
      const originalPrice = item.unitPrice || item.price || 0;
      return total + originalPrice * (item.quantity || 1);
    }, 0);
    
    // Calculate bulk discount savings directly
    const bulkSavings = cartItems.reduce((savings, item) => {
      const originalSinglePrice = item.unitPrice || item.price || 0;
      const effectivePrice = calculateEffectivePrice(item, item.quantity);

      if (originalSinglePrice > effectivePrice) {
        const savingPerItem = originalSinglePrice - effectivePrice;
        return savings + (savingPerItem * (item.quantity || 1));
      }
      return savings;
    }, 0);
    
    // Subtract discount amount from API (legacy fallback path when finalSubtotal missing)
    const bundleDiscount = discountCalculation ? (discountCalculation.totalDiscountAmount || 0) : 0;
    
    return originalSubtotal - bulkSavings - bundleDiscount;
  }, [cartItems, calculateEffectivePrice, discountCalculation]);

  // Get original subtotal (before any discounts)
  const getCartOriginalSubtotal = useCallback(() => {
    if (discountCalculation && typeof discountCalculation.originalSubtotal === 'number') {
      return discountCalculation.originalSubtotal;
    }

    // Always calculate from original prices, regardless of API
    return cartItems.reduce((total, item) => {
      const originalPrice = item.unitPrice || item.price || 0;
      return total + originalPrice * (item.quantity || 1);
    }, 0);
  }, [cartItems, discountCalculation]);

  // Get total discount amount from the discount system
  const getCartDiscountAmount = useCallback(() => {
    if (discountCalculation) {
      return discountCalculation.totalDiscountAmount || 0;
    }
    return 0;
  }, [discountCalculation]);

  // Get applied discounts information
  const getAppliedDiscounts = useCallback(() => {
    if (discountCalculation && discountCalculation.appliedDiscounts) {
      return discountCalculation.appliedDiscounts;
    }
    return [];
  }, [discountCalculation]);

  // Get item discount information
  const getItemDiscountInfo = useCallback((itemId) => {
    const cartItem = cartItems.find(item => item.id === itemId);
    const serverLine = discountCalculation?.cartItems?.find(
      (c) => String(c.productId) === String(itemId)
    );
    const primaryType = discountCalculation?.primaryCartDiscountType ?? null;

    if (!cartItem && !serverLine) {
      return {
        discountedPrice: null,
        hasAppliedDiscount: false,
        originalPrice: null,
        cartPromotionPercent: null,
        hasCartPromotion: false,
        hasBulkPriceTier: false,
      };
    }

    const originalPrice = cartItem
      ? Number(cartItem.unitPrice || cartItem.price || 0)
      : (serverLine?.price != null ? Number(serverLine.price) : 0);

    if (!discountCalculation) {
      const bulkDiscounted = cartItem
        ? Number(calculateEffectivePrice(cartItem, cartItem.quantity))
        : originalPrice;
      const hasBulk = bulkDiscounted < originalPrice - 0.0001;
      return {
        discountedPrice: hasBulk ? bulkDiscounted : null,
        hasAppliedDiscount: hasBulk,
        originalPrice,
        cartPromotionPercent: null,
        hasCartPromotion: false,
        hasBulkPriceTier: hasBulk,
      };
    }

    if (primaryType === 'COUPON') {
      return {
        discountedPrice: null,
        hasAppliedDiscount: false,
        originalPrice,
        cartPromotionPercent: null,
        hasCartPromotion: false,
        hasBulkPriceTier: false,
      };
    }

    if (primaryType === 'CART_PROMOTION') {
      const qty = serverLine?.quantity ?? cartItem?.quantity ?? 1;
      const campaignAmt =
        serverLine?.cartPromotionDiscountAmount != null
          ? Number(serverLine.cartPromotionDiscountAmount)
          : 0;
      const hasCampaign = campaignAmt > 0;
      const unitAfterCampaign = hasCampaign
        ? originalPrice - campaignAmt / Math.max(1, qty)
        : originalPrice;

      return {
        discountedPrice: hasCampaign ? unitAfterCampaign : null,
        hasAppliedDiscount: hasCampaign,
        originalPrice,
        cartPromotionPercent: serverLine?.cartPromotionDiscountPercent ?? null,
        hasCartPromotion: hasCampaign,
        hasBulkPriceTier: false,
      };
    }

    if (primaryType === 'BULK_DISCOUNT') {
      const qty = serverLine?.quantity ?? cartItem?.quantity ?? 1;
      const bulkAmt =
        serverLine?.bulkDiscountAmount != null
          ? Number(serverLine.bulkDiscountAmount)
          : 0;
      const hasBulk = bulkAmt > 0;
      const unitAfterBulk = hasBulk
        ? originalPrice - bulkAmt / Math.max(1, qty)
        : originalPrice;

      return {
        discountedPrice: hasBulk ? unitAfterBulk : null,
        hasAppliedDiscount: hasBulk,
        originalPrice,
        cartPromotionPercent: null,
        hasCartPromotion: false,
        hasBulkPriceTier: hasBulk,
      };
    }

    return {
      discountedPrice: null,
      hasAppliedDiscount: false,
      originalPrice,
      cartPromotionPercent: null,
      hasCartPromotion: false,
      hasBulkPriceTier: false,
    };
  }, [cartItems, calculateEffectivePrice, discountCalculation]);

  // Calculate tax amount - use calculate endpoint data when available
  const getCartTaxAmount = useCallback(() => {
    // Use tax amount from calculate endpoint if available
    if (discountCalculation && typeof discountCalculation.taxAmount === 'number') {
      return discountCalculation.taxAmount;
    }
    
    // Fallback to local calculation
    const subtotal = getCartSubtotal();
    return subtotal * taxAndShippingRates.taxRate;
  }, [getCartSubtotal, taxAndShippingRates.taxRate, discountCalculation]);

  // Calculate shipping cost - use calculate endpoint data when available
  const getCartShippingCost = useCallback(() => {
    // Use shipping cost from calculate endpoint if available
    if (discountCalculation && typeof discountCalculation.shippingCost === 'number') {
      return discountCalculation.shippingCost;
    }
    
    // Fallback to local calculation
    const subtotal = getCartSubtotal();

    // No shipping cost if cart is empty
    if (cartItems.length === 0) {
      return 0;
    }

    // Free shipping is not offered — always use configured shipping cost when cart has items.
    return taxAndShippingRates.shippingCost;
  }, [getCartSubtotal, taxAndShippingRates.shippingCost, cartItems.length, discountCalculation]);

  // Calculate total including tax and shipping - use calculate endpoint data when available
  const getCartTotal = useCallback(() => {
    // Use final total from calculate endpoint if available
    if (discountCalculation && typeof discountCalculation.finalTotal === 'number') {
      return discountCalculation.finalTotal;
    }
    
    // Fallback to local calculation
    const subtotal = getCartSubtotal();
    const taxAmount = getCartTaxAmount();
    const shippingCost = getCartShippingCost();

    return subtotal + taxAmount + shippingCost;
  }, [getCartSubtotal, getCartTaxAmount, getCartShippingCost, discountCalculation]);

  // Calculate savings from bulk discounts
  const getCartSavings = useCallback(() => {
    // When server discount calculation exists, bulk/campaign values are already included
    // in appliedDiscounts + totalDiscountAmount. Returning local savings would duplicate UI rows.
    if (discountCalculation) {
      return 0;
    }

    return cartItems.reduce((savings, item) => {
      const originalSinglePrice = item.unitPrice || item.price || 0;
      const effectivePrice = calculateEffectivePrice(item, item.quantity);

      if (originalSinglePrice > effectivePrice) {
        const savingPerItem = originalSinglePrice - effectivePrice;
        return savings + (savingPerItem * (item.quantity || 1));
      }
      return savings;
    }, 0);
  }, [cartItems, calculateEffectivePrice, discountCalculation]);

  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + (item.quantity || 0), 0);
  }, [cartItems]);

  const isEligibleForFreeShipping = useCallback(() => false, []);

  const amountNeededForFreeShipping = useCallback(() => 0, []);

  const clearCart = useCallback(async () => {
    console.log('🗑️ Clearing cart...');
    
    // Prevent multiple simultaneous clear operations
    if (isSyncing || isClearingCart) {
      console.log('🚫 Clear cart blocked - already syncing or clearing');
      return;
    }
    
    // Set clearing flag to prevent sync useEffect from triggering
    setIsClearingCart(true);
    
    try {
      // Clear local state immediately for better UX
      setCartItems([]);
      setDiscountCalculation(null);
      setCheckoutDiscountCode('');
      
      // Reset sync tracking to ensure proper state
      lastSyncedCart.current = '[]'; // Immediately mark as synced to prevent useEffect trigger
      lastSyncTime.current = Date.now(); // Set sync time to prevent immediate re-sync

      // If user is authenticated, explicitly sync empty cart with server
      if (isAuthenticated) {
        setIsSyncing(true);
        try {
          await cartApi.syncCart([]);
          console.log('✅ Empty cart synced with server');
        } catch (error) {
          console.error('❌ Failed to sync empty cart with server:', error);
          // Don't revert on failure to avoid infinite loops
        } finally {
          setIsSyncing(false);
        }
      }
    } finally {
      // Always reset clearing flag
      setIsClearingCart(false);
    }
  }, [isAuthenticated, isSyncing, isClearingCart]);

  // Set customer email for personalized discounts
  const setCustomerEmailForDiscounts = useCallback((email) => {
    setCustomerEmail(email);
    // Recalculate discounts with the new email (debounced)
    if (cartItems.length > 0) {
      debouncedCalculateCartDiscounts();
    }
  }, [cartItems.length, debouncedCalculateCartDiscounts]);

  // Manual discount recalculation (useful for checkout page)
  const recalculateDiscounts = useCallback(() => {
    calculateCartDiscounts();
  }, [calculateCartDiscounts]);

  const contextValue = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    isCartOperationPending,

    // Pricing calculations
    getCartSubtotal,
    getCartOriginalSubtotal,
    getCartTaxAmount,
    getCartShippingCost,
    getCartTotal,
    getCartSavings,
    getCartItemCount,

    // Discount system
    getCartDiscountAmount,
    getAppliedDiscounts,
    getItemDiscountInfo,
    discountCalculation,
    discountLoading,
    customerEmail,
    setCustomerEmailForDiscounts,
    calculateCartDiscounts,
    recalculateDiscounts,
    checkoutDiscountCode,
    applyCheckoutDiscountCode,
    clearCheckoutDiscountCode,

    // User authentication integration
    isLoadingUserCart,
    hasLoadedUserCart,
    syncCartWithServer,
    isSyncing,
    isClearingCart,

    // Shipping calculations
    isEligibleForFreeShipping,
    amountNeededForFreeShipping,

    // Configuration
    taxAndShippingRates,
    ratesLoading,

    // Utilities
    clearCart,
    calculateEffectivePrice,

    // Add-to-cart feedback drawer
    cartFeedback,
    showCartAddedFeedback,
    closeCartAddedFeedback,
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
      <CartAddedDrawer />
    </CartContext.Provider>
  );
};